var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import MediaQuery from 'react-responsive';
import { useResponsiveProps, useIsMounted, } from '@telia-front/helper';
import { useTheme } from '@telia-front/theme-provider';
import TableCell from '../table-cell/table-cell';
import TableRow from '../table-row/table-row';
const Table = (props) => {
    const { responsiveProps } = props, rest = __rest(props, ["responsiveProps"]);
    return responsiveProps ? React.createElement(TableInnerResponsive, Object.assign({}, rest, { responsiveProps: responsiveProps })) : React.createElement(TableInner, Object.assign({}, rest));
};
const TableInnerResponsive = (props) => {
    const { responsiveProps } = props, rest = __rest(props, ["responsiveProps"]);
    const { breakPoints } = useTheme();
    const responsive = useResponsiveProps({ breakPoints, responsive: responsiveProps });
    return React.createElement(TableInner, Object.assign({}, rest, responsive));
};
const TableInner = (props) => {
    const { break: breakpoint = 'sm', responsive, hover, stickyHeader, className, bordered, maxHeight, minWidth, children, onScroll, elementRef, } = props;
    const { breakPoints } = useTheme();
    const tableRef = React.useRef(null);
    const [isSticky, setIsSticky] = React.useState(false);
    const isMounted = useIsMounted();
    const BEM = () => {
        const classArray = ['table'];
        if (responsive) {
            classArray.push('table--responsive');
        }
        if (hover) {
            classArray.push('table--hover');
        }
        if (bordered) {
            classArray.push('table--bordered');
        }
        if (stickyHeader) {
            classArray.push(`table--sticky-header-${stickyHeader}`);
            if (isSticky) {
                classArray.push('is-sticky');
            }
        }
        if (className) {
            classArray.push(className);
        }
        return classArray.join(' ');
    };
    const currentBreakpoint = React.useMemo(() => {
        if (!responsive) {
            return 0;
        }
        return breakPoints[breakpoint];
    }, [breakPoints, breakpoint, responsive]);
    const handleScroll = (e) => {
        const { current } = tableRef;
        const isBroken = breakpoint && window.matchMedia(`(max-width: ${currentBreakpoint - 1}px)`).matches;
        if (current && !isBroken) {
            const currentIsSticky = current.scrollTop > 0;
            setIsSticky(currentIsSticky);
        }
        if (onScroll) {
            onScroll(e);
        }
    };
    const getCleanTable = React.useMemo(() => {
        // Remove whitespace added via Wordpress and react-jsx-parser
        const tableSections = React.Children.map(children, (child, childIndex) => {
            if (React.isValidElement(child) && (child.type === 'thead' || child.type === 'tbody')) {
                const rows = React.Children.map(child.props.children, (tr, trIndex) => {
                    if (!React.isValidElement(tr)) {
                        return null;
                    }
                    if (tr.type === 'tr' || tr.type === TableRow) {
                        // handle normal row
                        const cells = React.Children.map(tr.props.children, (td) => {
                            if (React.isValidElement(td) && (td.type === 'td' || td.type === 'th' || td.type === TableCell)) {
                                return td;
                            }
                            return null;
                        });
                        const newRowProps = {
                            children: cells,
                            key: trIndex,
                        };
                        return React.cloneElement(tr, newRowProps);
                    }
                    return null;
                });
                const newChildProps = {
                    children: rows,
                    key: childIndex,
                };
                const newChild = React.cloneElement(child, newChildProps);
                return newChild;
            }
            return null;
        });
        return tableSections;
    }, [children]);
    const getTableDataCols = React.useMemo(() => {
        const cleanedTable = getCleanTable;
        const tables = [];
        const thead = [];
        let footer = null;
        cleanedTable.forEach((child) => {
            if (!React.isValidElement(child)) {
                return null;
            }
            React.Children.forEach(child.props.children, (tr, trIndex) => {
                if (!React.isValidElement(tr) || tr.props.type === 'highlight-header') {
                    return null;
                }
                let cellIndex = 0;
                const rowChildrenCount = React.Children.count(tr.props.children);
                const rowChildrenAsArray = React.Children.toArray(tr.props.children);
                // Create table (exclude thead)
                if (child.type === 'tbody' &&
                    !tables[trIndex]) {
                    tables[trIndex] = [];
                }
                if ((tr.props['data-footer'] || tr.props.type === 'footer') && rowChildrenCount <= 2) {
                    footer = {
                        cells: [rowChildrenAsArray[0], rowChildrenAsArray[rowChildrenCount - 1]],
                        row: tr,
                    };
                }
                else {
                    React.Children.forEach(rowChildrenAsArray, (td) => {
                        if (!React.isValidElement(td)) {
                            return null;
                        }
                        // Store theads
                        if (child.type === 'thead') {
                            thead.push(td);
                            return;
                        }
                        // Shift table index until it finds empty one (avoid rows filled by rowSpan - avoid row overrides)
                        if (tables[trIndex][cellIndex]) {
                            do {
                                cellIndex += 1;
                            } while (tables[trIndex][cellIndex]);
                        }
                        // Add cell and duplicate it in other tables (duplicate rowSpan cells between tables)
                        const tableRepeat = td.props.rowSpan || 1;
                        for (let i = 0; i < tableRepeat; i += 1) {
                            // create table row
                            const row = child.props.children[trIndex + i];
                            // Create table if not defined
                            if (!tables[trIndex + i]) {
                                tables[trIndex + i] = [];
                            }
                            // add row to table
                            tables[trIndex + i][cellIndex] = { cells: [thead[cellIndex], td], row };
                            // has col span
                            if (td.props.colSpan) {
                                for (let j = 1; j < td.props.colSpan; j += 1) {
                                    tables[trIndex + i][cellIndex + j] = { cells: [thead[cellIndex + j]], row };
                                }
                            }
                        }
                        if ((td.props['data-headless'] || td.props.headless) && td.props.colSpan) {
                            for (let i = 0; i < td.props.colSpan; i++) {
                                if (cellIndex + i !== cellIndex) {
                                    delete tables[trIndex][cellIndex + i];
                                }
                            }
                        }
                    });
                }
            });
        });
        if (footer) {
            tables[tables.length - 1] = [];
            tables[tables.length - 1].push(footer);
        }
        return tables;
    }, [getCleanTable]);
    const getTableDataRows = React.useMemo(() => {
        const cleanedTable = getCleanTable;
        const tables = [];
        let rowIndex = 0; // flatten thead and tbody row indexes
        let footer = null;
        cleanedTable.forEach((child) => {
            if (!React.isValidElement(child)) {
                return null;
            }
            React.Children.forEach(child.props.children, (tr, trIndex) => {
                if (!React.isValidElement(tr)) {
                    return null;
                }
                // Index th/td cells as tables index (excludes row headers and fast forwards with colsPan)
                let tableIndex = 0;
                const rowChildrenCount = React.Children.count(tr.props.children);
                const rowChildrenAsArray = React.Children.toArray(tr.props.children);
                if (tr.props['data-footer'] || tr.props.type === 'footer') {
                    footer = {
                        cells: [rowChildrenAsArray[0], rowChildrenAsArray[rowChildrenCount - 1]],
                        row: tr,
                    };
                }
                else {
                    React.Children.forEach(rowChildrenAsArray, (td, tdIndex) => {
                        if (!React.isValidElement(td)) {
                            return null;
                        }
                        // Shift table based on th colSpan
                        if (tdIndex === 0 && td.props.colSpan) {
                            tableIndex += td.props.colSpan - 1;
                        }
                        // No table for row header
                        if (tdIndex === 0) {
                            return;
                        }
                        // Create table
                        if (!tables[tableIndex]) {
                            tables[tableIndex] = [];
                        }
                        // Shift table index until it finds empty one (avoid rows filled by rowSpan - avoid row overrides)
                        if (tables[tableIndex][rowIndex]) {
                            do {
                                tableIndex += 1;
                            } while (tables[tableIndex][rowIndex]);
                        }
                        // Add cell and duplicate it in other tables (duplicate colSpan cells between tables)
                        const tableRepeat = td.props.colSpan || 1;
                        for (let i = 0; i < tableRepeat; i += 1) {
                            // create table row
                            // Create table if not defined (missing thead)
                            if (!tables[tableIndex]) {
                                tables[tableIndex] = [];
                            }
                            // add row to table
                            tables[tableIndex][rowIndex] = {
                                cells: [rowChildrenAsArray[0], td],
                                row: tr,
                                type: (child.type === 'thead') ? 'thead' : 'tbody',
                            };
                            // has row span
                            if (td.props.rowSpan) {
                                // Fill table's next rows based on rowSpan
                                for (let j = 1; j < td.props.rowSpan; j += 1) {
                                    // Using trIndex over rowIndex since child specific request
                                    const row = child.props.children[trIndex + j];
                                    tables[tableIndex][rowIndex + j] = {
                                        cells: [row.props.children[0] || row.props.children],
                                        row,
                                        type: (child.type === 'thead') ? 'thead' : 'tbody',
                                    };
                                }
                            }
                            // shift table index
                            tableIndex += 1;
                        }
                    });
                }
                // shift row index
                rowIndex += 1;
            });
        });
        if (footer) {
            tables[tables.length] = [];
            tables[tables.length - 1].push(footer);
        }
        return tables;
    }, [getCleanTable]);
    const isHeadless = (row) => {
        return (row.row.props['data-headless'] || row.row.props.headless) || (row.cells[1] &&
            (row.cells[1].props['data-headless'] || row.cells[1].props.headless));
    };
    const genThead = (thead, key) => {
        return (React.createElement("thead", { key: key }, thead));
    };
    const genTbody = (tbody, key, className) => {
        return (React.createElement("tbody", { key: key, className: className }, tbody));
    };
    const getTableData = React.useMemo(() => {
        switch (responsive) {
            case 'rows':
                return getTableDataRows;
            case 'cols':
                return getTableDataCols;
            default:
                return [];
        }
    }, [getTableDataCols, getTableDataRows, responsive]);
    const getAltTable = React.useMemo(() => {
        const tablesData = getTableData;
        const newTable = [];
        tablesData.forEach((tableData, tableIndex) => {
            const thead = {
                children: [],
                type: 'thead',
            };
            const bodyClassArray = [];
            const tbody = {
                children: [],
                className: undefined,
                type: 'tbody',
            };
            newTable[tableIndex] = [];
            tableData.forEach((rowData, index) => {
                // Add classNames to tbody from TableRows
                if (rowData.row.props.bodyClassName) {
                    bodyClassArray.push(rowData.row.props.bodyClassName);
                }
                const headless = isHeadless(rowData);
                const colSpan = (headless) ? 2 : 0;
                const cell1 = (!headless) ? rowData.cells[0] : null;
                const cell2 = rowData.cells[1];
                let newCell1 = null;
                let newCell2 = null;
                // Cell1
                if (cell1) {
                    const newCell1Props = {
                        colSpan: 0,
                        key: 0,
                    };
                    newCell1 = React.cloneElement(cell1, newCell1Props);
                }
                // Cell2
                if (cell2) {
                    let newCell2Props = {
                        colSpan,
                        key: 1,
                    };
                    if (responsive === 'cols') {
                        // colSpan => rowSpan
                        newCell2Props = {
                            colSpan,
                            key: 1,
                            rowSpan: (cell2.props['data-headless'] || cell2.props.headless) ? 1 : cell2.props.colSpan,
                        };
                    }
                    newCell2 = React.cloneElement(cell2, newCell2Props);
                }
                // Row1
                const newRowProps = {
                    children: [
                        newCell1,
                        newCell2,
                    ],
                    key: index,
                };
                const newRow = React.cloneElement(rowData.row, newRowProps);
                // Don't show empty rows
                if (cell2 && !cell2.props.children) {
                    return;
                }
                if (newRow.props['data-table-collapse'] || newRow.props.collapsible) {
                    bodyClassArray.push('table-collapse__body');
                }
                if (rowData.type === 'thead') {
                    thead.children.push(newRow);
                }
                else {
                    tbody.children.push(newRow);
                }
            });
            tbody.className = bodyClassArray.join(' ');
            newTable[tableIndex] = [thead, tbody];
        });
        const blocks = newTable
            .reduce((arr, curr) => {
            return arr.concat(curr);
        }, [])
            .map((block, i) => {
            if (block.type === 'thead') {
                return genThead(block.children, i);
            }
            else {
                return genTbody(block.children, i, block.className);
            }
        });
        return (React.createElement("table", null, blocks));
    }, [getTableData, responsive]);
    const getDynamicTable = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(MediaQuery, { minWidth: currentBreakpoint }, getDefaultTable(true)),
            React.createElement(MediaQuery, { maxWidth: currentBreakpoint - 1 },
                React.createElement("div", { className: "table__responsive" }, responsive && getAltTable))));
    };
    const getDefaultTable = (hasBreakProp) => {
        const tableContent = hasBreakProp ? getCleanTable : children;
        return (React.createElement("div", { className: "table__original" },
            React.createElement("table", { style: minWidth ? { minWidth: typeof minWidth === 'string' ? minWidth : `${minWidth}px` } : undefined }, tableContent)));
    };
    const tableStyle = React.useMemo(() => {
        let styles = {};
        if (maxHeight) {
            styles = Object.assign(Object.assign({}, styles), { maxHeight });
        }
        return styles;
    }, [maxHeight]);
    React.useEffect(() => {
        if (elementRef && tableRef.current) {
            elementRef(tableRef.current);
        }
    }, [elementRef, tableRef]);
    return (React.createElement("div", { className: BEM(), style: tableStyle, ref: tableRef, onScroll: handleScroll }, (isMounted && responsive) ? getDynamicTable() : getDefaultTable(false)));
};
export { Table as default, };
